import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { readStoredSiteControls } from "@/stores/siteControls";

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/",
      name: "home",
      component: () => import("@/pages/Home.vue"),
    },
    // Auth Routes
    {
      path: "/login",
      name: "login",
      component: () => import("@/pages/auth/Login.vue"),
      meta: { guest: true },
    },
    {
      path: "/confirm-email",
      name: "confirm-email",
      component: () => import("@/pages/auth/ConfirmEmail.vue"),
    },
    // Account Routes (require auth but NOT credentials)
    {
      path: "/account",
      name: "account",
      component: () => import("@/pages/account/Profile.vue"),
      meta: { requiresAuth: true, accountTab: "portfolio" },
    },
    {
      path: "/account/info",
      name: "account-info",
      component: () => import("@/pages/account/Profile.vue"),
      meta: { requiresAuth: true, accountTab: "account" },
    },
    {
      path: "/account/settings",
      name: "account-settings",
      component: () => import("@/pages/account/Profile.vue"),
      meta: { requiresAuth: true, accountTab: "settings" },
    },
    {
      path: "/account/security",
      name: "account-security",
      component: () => import("@/pages/account/Profile.vue"),
      meta: { requiresAuth: true, accountTab: "security" },
    },
    {
      path: "/account/address-book",
      name: "address-book",
      component: () => import("@/pages/account/Profile.vue"),
      meta: { requiresAuth: true, accountTab: "address-book" },
    },
    {
      path: "/account/activity",
      name: "account-activity",
      component: () => import("@/pages/account/Profile.vue"),
      meta: { requiresAuth: true, accountTab: "activity" },
    },
    {
      path: "/account/sessions",
      name: "account-sessions",
      redirect: { name: "account-security" },
    },
    // ── Financial Instruments (require auth + KYC credential) ──
    // Loan Contract Routes
    {
      path: "/loan",
      name: "loan",
      component: () => import("@/pages/contracts/loan/Index.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "Loans",
      },
    },
    {
      path: "/loan/create",
      name: "loan-create",
      component: () => import("@/pages/contracts/loan/Create.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "Loan Creation",
      },
    },
    {
      path: "/loan/:id",
      name: "loan-view",
      component: () => import("@/pages/contracts/loan/View.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "Loan Details",
      },
    },
    // CDO Bond Routes
    {
      path: "/cdo",
      name: "cdo",
      component: () => import("@/pages/contracts/cdo/Index.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "Bonds",
      },
    },
    {
      path: "/cdo/create",
      name: "cdo-create",
      component: () => import("@/pages/contracts/cdo/Create.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "Bond Creation",
      },
    },
    {
      path: "/cdo/:id",
      name: "cdo-view",
      component: () => import("@/pages/contracts/cdo/View.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "Bond Details",
      },
    },
    // Auction Routes
    {
      path: "/auction",
      name: "auction",
      component: () => import("@/pages/contracts/auction/Index.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "Auctions",
      },
    },
    {
      path: "/auction/create",
      name: "auction-create",
      component: () => import("@/pages/contracts/auction/Create.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "Auction Creation",
      },
    },
    // SAFE Fundraising Routes
    {
      path: "/safe",
      name: "safe",
      component: () => import("@/pages/contracts/safe/Index.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "Fundraising",
      },
    },
    {
      path: "/safe/create",
      name: "safe-create",
      component: () => import("@/pages/contracts/safe/Create.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "SAFE Creation",
      },
    },
    // Fractionalization Routes
    {
      path: "/fracture",
      name: "fracture",
      component: () => import("@/pages/contracts/fracture/Index.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "Fractionalization",
      },
    },
    {
      path: "/fracture/create",
      name: "fracture-create",
      component: () => import("@/pages/contracts/fracture/Create.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "Fractionalization",
      },
    },
    // Credit Default Swap Routes
    {
      path: "/cds",
      name: "cds",
      component: () => import("@/pages/contracts/cds/Index.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "Credit Swaps",
      },
    },
    {
      path: "/cds/create",
      name: "cds-create",
      component: () => import("@/pages/contracts/cds/Create.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "CDS Creation",
      },
    },
    // Trust Fund Routes
    {
      path: "/trust-fund",
      name: "trust-fund",
      component: () => import("@/pages/contracts/trust-fund/Index.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "Trust Funds",
      },
    },
    {
      path: "/trust-fund/create",
      name: "trust-fund-create",
      component: () => import("@/pages/contracts/trust-fund/Create.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "Trust Fund Creation",
      },
    },
    // Smart Assets Routes
    {
      path: "/assets",
      name: "assets",
      component: () => import("@/pages/assets/Index.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "Smart Assets",
      },
    },
    {
      path: "/assets/mint/:type",
      name: "assets-mint",
      component: () => import("@/pages/assets/Mint.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "Asset Minting",
      },
    },
    // Trade Routes
    {
      path: "/trade",
      name: "trade",
      component: () => import("@/pages/contracts/trade/Index.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "Trading",
      },
    },
    {
      path: "/trade/create",
      name: "trade-create",
      component: () => import("@/pages/contracts/trade/Create.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "Trade Creation",
      },
    },
    {
      path: "/trade/:id",
      name: "trade-view",
      component: () => import("@/pages/contracts/trade/View.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "Trade Details",
      },
    },
    // ── Smart Credentials (always accessible — this is where users GET credentials) ──
    {
      path: "/credentials",
      name: "credentials",
      component: () => import("@/pages/credentials/Index.vue"),
    },
    {
      path: "/credentials/individual",
      name: "credentials-individual",
      component: () => import("@/pages/credentials/Individual.vue"),
    },
    {
      path: "/credentials/business",
      name: "credentials-business",
      component: () => import("@/pages/credentials/Business.vue"),
    },
    {
      path: "/credentials/catalog",
      name: "credentials-catalog",
      component: () => import("@/pages/credentials/Catalog.vue"),
    },
    {
      path: "/credentials/request",
      name: "credentials-request",
      component: () => import("@/pages/credentials/Request.vue"),
    },
    {
      path: "/credentials/requests/:caseId",
      name: "credentials-case-status",
      component: () => import("@/pages/credentials/CaseStatus.vue"),
      meta: { requiresAuth: true, featureLabel: "Credential Request Status" },
    },
    // ── Credential Operations (authenticated) ──
    {
      path: "/ops/credentials/queue",
      name: "ops-credentials-queue",
      component: () => import("@/pages/ops/credentials/Queue.vue"),
      meta: { requiresAuth: true, featureLabel: "Credential Ops Queue" },
    },
    {
      path: "/ops/credentials/cases/:caseId",
      name: "ops-credentials-case",
      component: () => import("@/pages/ops/credentials/Case.vue"),
      meta: { requiresAuth: true, featureLabel: "Credential Ops Case" },
    },
    {
      path: "/ops/disclosures/requests/:id",
      name: "ops-disclosure-request",
      component: () => import("@/pages/ops/disclosures/Request.vue"),
      meta: { requiresAuth: true, featureLabel: "Disclosure Request" },
    },
    // ── Simulation Routes (authenticated accounts only) ──
    {
      path: "/simulation",
      name: "simulation",
      component: () => import("@/pages/simulation/Index.vue"),
      meta: { requiresAuth: true, featureLabel: "Simulation" },
    },
    {
      path: "/simulation/config",
      name: "simulation-config",
      component: () => import("@/pages/simulation-config/Index.vue"),
      meta: { requiresAuth: true, featureLabel: "Simulation Config" },
    },
    // Tab-based navigation: /simulation/config/wallets, /simulation/config/loans, etc.
    {
      path: "/simulation/config/:tabOrId",
      name: "simulation-config-tab",
      component: () => import("@/pages/simulation-config/Index.vue"),
      meta: { requiresAuth: true, featureLabel: "Simulation Config" },
    },
    {
      path: "/simulation/loan/:id",
      name: "simulation-loan-view",
      component: () => import("@/pages/contracts/loan/View.vue"),
      props: { isTestMode: true },
      meta: { requiresAuth: true, featureLabel: "Simulation Loan" },
    },
    {
      path: "/simulation/trade/:id",
      name: "simulation-trade-view",
      component: () => import("@/pages/contracts/trade/View.vue"),
      meta: { requiresAuth: true, featureLabel: "Simulation Trade" },
    },
    {
      path: "/simulation/clo/:id",
      name: "simulation-clo-view",
      component: () => import("@/pages/contracts/cdo/View.vue"),
      props: { isTestMode: true },
      meta: { requiresAuth: true, featureLabel: "Simulation CLO" },
    },
    // Gold Route
    {
      path: "/gold",
      name: "gold",
      component: () => import("@/pages/gold/Index.vue"),
      meta: {
        requiresAuth: true,
        requiresCredential: true,
        featureLabel: "Digital Gold",
      },
    },
    // Market Route
    {
      path: "/market",
      name: "market",
      component: () => import("@/pages/market/Index.vue"),
    },
    {
      path: "/market/bonds",
      name: "market-bonds",
      component: () => import("@/pages/market/Bonds.vue"),
    },
    {
      path: "/market/trades",
      name: "market-trades",
      component: () => import("@/pages/market/Trades.vue"),
    },
    {
      path: "/market/auctions",
      name: "market-auctions",
      component: () => import("@/pages/market/Auctions.vue"),
    },
    {
      path: "/market/loans",
      name: "market-loans",
      component: () => import("@/pages/market/Loans.vue"),
    },
    {
      path: "/market/fund-raising",
      name: "market-fund-raising",
      component: () => import("@/pages/market/FundRaising.vue"),
    },
    // Analytics Route
    {
      path: "/analytics",
      name: "analytics",
      component: () => import("@/pages/analytics/Index.vue"),
    },
    // Admin Route
    {
      path: "/admin",
      name: "admin",
      component: () => import("@/pages/admin/Index.vue"),
      meta: { requiresAuth: true, featureLabel: "Admin" },
    },
    {
      path: "/admin/credentials/templates",
      name: "admin-credentials-templates",
      component: () => import("@/pages/admin/credentials/Templates.vue"),
      meta: { requiresAuth: true, featureLabel: "Credential Templates" },
    },
    {
      path: "/admin/credentials/policies",
      name: "admin-credentials-policies",
      component: () => import("@/pages/admin/credentials/Policies.vue"),
      meta: { requiresAuth: true, featureLabel: "Credential Policies" },
    },
    {
      path: "/admin/credentials/tenants",
      name: "admin-credentials-tenants",
      component: () => import("@/pages/admin/credentials/Tenants.vue"),
      meta: { requiresAuth: true, featureLabel: "Credential Tenants" },
    },
    // Contract documentation
    {
      path: "/contracts/documentation",
      name: "contracts-documentation",
      component: () => import("@/pages/contracts/documentation/index.vue"),
    },
    {
      path: "/contracts/documentation/loan",
      name: "contracts-documentation-loan",
      component: () => import("@/pages/contracts/documentation/loan/index.vue"),
    },
    {
      path: "/contracts/documentation/clo-bond",
      name: "contracts-documentation-clo-bond",
      component: () =>
        import("@/pages/contracts/documentation/clo-bond/index.vue"),
    },
    {
      path: "/contracts/documentation/trade",
      name: "contracts-documentation-trade",
      component: () =>
        import("@/pages/contracts/documentation/trade/index.vue"),
    },
    {
      path: "/contracts/documentation/credential-vending",
      name: "contracts-documentation-credential-vending",
      component: () =>
        import("@/pages/contracts/documentation/credential-vending/index.vue"),
    },
    {
      path: "/contracts/documentation/credit-default-swap",
      name: "contracts-documentation-credit-default-swap",
      component: () =>
        import("@/pages/contracts/documentation/credit-default-swap/index.vue"),
    },
    {
      path: "/contracts/documentation/trust-fund",
      name: "contracts-documentation-trust-fund",
      component: () =>
        import("@/pages/contracts/documentation/trust-fund/index.vue"),
    },
    {
      path: "/contracts/documentation/options",
      name: "contracts-documentation-options",
      component: () =>
        import("@/pages/contracts/documentation/options/index.vue"),
    },
    {
      path: "/contracts/documentation/auction",
      name: "contracts-documentation-auction",
      component: () =>
        import("@/pages/contracts/documentation/auction/index.vue"),
    },
    {
      path: "/contracts/documentation/safe",
      name: "contracts-documentation-safe",
      component: () => import("@/pages/contracts/documentation/safe/index.vue"),
    },
    {
      path: "/contracts/documentation/fracture",
      name: "contracts-documentation-fracture",
      component: () =>
        import("@/pages/contracts/documentation/fracture/index.vue"),
    },
    {
      path: "/contracts/documentation/mint-smart-asset",
      name: "contracts-documentation-mint-smart-asset",
      component: () =>
        import("@/pages/contracts/documentation/mint-smart-asset/index.vue"),
    },
    {
      path: "/contracts/documentation/insurance-pool",
      name: "contracts-documentation-insurance-pool",
      component: () =>
        import("@/pages/contracts/documentation/insurance-pool/index.vue"),
    },
  ],
});

// Navigation guards
router.beforeEach(async (to, _from, next) => {
  const auth = useAuthStore();

  // Wait for auth initialization to finish before checking guards.
  // This prevents the race where the guard redirects to /login before
  // persisted tokens have been restored from localStorage.
  if (!auth.isInitialized) {
    await auth.initPromise;
  }

  // 1. Auth gate: redirect to login if route requires authentication
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    next({ name: "login", query: { redirect: to.fullPath } });
    return;
  }

  // 2. Guest gate: redirect to home if already logged in
  if (to.meta.guest && auth.isAuthenticated) {
    next({ name: "home" });
    return;
  }

  const controls = readStoredSiteControls();
  const isAdminRoute = to.path.startsWith("/admin");
  const isLoginRoute = to.path.startsWith("/login");
  const isAccountRoute = to.path.startsWith("/account");
  const isHomeRoute = to.path === "/";

  if (
    controls.maintenanceMode &&
    !isAdminRoute &&
    !isLoginRoute &&
    !isAccountRoute &&
    !isHomeRoute
  ) {
    next(
      auth.isAuthenticated
        ? {
            name: "admin",
            query: { section: "controls", blocked: "maintenance" },
          }
        : { name: "home" },
    );
    return;
  }

  const blockedByControl =
    (!controls.enableSimulation && to.path.startsWith("/simulation")) ||
    (!controls.enableAnalytics && to.path.startsWith("/analytics")) ||
    (!controls.enableMarket && to.path.startsWith("/market")) ||
    (!controls.enableCredentials && to.path.startsWith("/credentials")) ||
    (!controls.enableDocumentation &&
      to.path.startsWith("/contracts/documentation")) ||
    (!controls.enableLoan && to.path.startsWith("/loan")) ||
    (!controls.enableBonds && to.path.startsWith("/cdo")) ||
    (!controls.enableTrade && to.path.startsWith("/trade")) ||
    (!controls.enableAssets && to.path.startsWith("/assets")) ||
    (!controls.enableGold && to.path.startsWith("/gold")) ||
    (!controls.enableAuction && to.path.startsWith("/auction")) ||
    (!controls.enableSafe && to.path.startsWith("/safe")) ||
    (!controls.enableFracture && to.path.startsWith("/fracture")) ||
    (!controls.enableCds && to.path.startsWith("/cds")) ||
    (!controls.enableTrustFund && to.path.startsWith("/trust-fund"));

  if (blockedByControl && !isAdminRoute) {
    next(
      auth.isAuthenticated
        ? { name: "admin", query: { section: "controls", blocked: "feature" } }
        : { name: "home" },
    );
    return;
  }

  // 3. Credential gate is handled by the CredentialGate component (not router redirect).
  //    This allows users to see the verification-required UI in context rather than
  //    being bounced to a separate page. The `requiresCredential` meta flag is consumed
  //    by the CredentialGate wrapper component on each page.

  next();
});

export default router;
